import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';

/** Cariye bağlı tek bir kayıt grubu (backend: AccountDeleteRelationDto) */
interface AccountDeleteRelation {
  code: string;
  label: string;
  count: number;
}

/** Silme öncesi kontrol sonucu (backend: AccountDeleteCheckDto) */
interface AccountDeleteCheck {
  accountId: number;
  accountCode: string;
  companyName: string;
  canDelete: boolean;
  balance: number;
  debitBalance: number;
  creditBalance: number;
  blockers: AccountDeleteRelation[];
  purgeable: AccountDeleteRelation[];
  totalBlockingRecords: number;
  totalPurgeableRecords: number;
}

interface DeleteAccountDialogProps {
  open: boolean;
  /** Silinecek carinin SQL Id'si (string olarak gelir) */
  accountId: string | null;
  accountName: string;
  accountCode?: string;
  onClose: () => void;
  /** Silme başarılı olduğunda listeyi yenilemek için çağrılır */
  onDeleted: (accountName: string) => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  accountId,
  accountName,
  accountCode,
  onClose,
  onDeleted
}) => {
  const { t } = useTranslation('accounting');

  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [check, setCheck] = useState<AccountDeleteCheck | null>(null);
  const [error, setError] = useState<string | null>(null);

  const numericId = accountId ? parseInt(accountId, 10) : NaN;

  const relationLabel = (relation: AccountDeleteRelation): string =>
    t(`current.deleteAccount.relations.${relation.code}`, { defaultValue: relation.label || relation.code });

  const runCheck = useCallback(async () => {
    if (!Number.isFinite(numericId)) {
      setError(t('current.deleteAccount.invalidId'));
      return;
    }

    setChecking(true);
    setError(null);
    setCheck(null);

    try {
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_DELETE_CHECK(numericId));
      const result = response.data;
      setCheck((result?.data || result) as AccountDeleteCheck);
    } catch (err: any) {
      console.error('❌ [DELETE-ACCOUNT] Silme kontrolü başarısız:', err);
      setError(handleApiError(err));
    } finally {
      setChecking(false);
    }
  }, [numericId, t]);

  useEffect(() => {
    if (open) {
      runCheck();
    } else {
      setCheck(null);
      setError(null);
      setDeleting(false);
    }
  }, [open, runCheck]);

  const handleDelete = async () => {
    if (!Number.isFinite(numericId) || !check?.canDelete) return;

    setDeleting(true);
    setError(null);

    try {
      await accountingApi.post(ACCOUNTING_API.ACCOUNT_HARD_DELETE(numericId));
      onDeleted(accountName);
      onClose();
    } catch (err: any) {
      console.error('❌ [DELETE-ACCOUNT] Kalıcı silme başarısız:', err);
      setError(handleApiError(err));
      // Sunucu tarafında yeni bir bağlı kayıt oluşmuş olabilir; durumu yenile
      await runCheck();
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = Boolean(check?.canDelete) && !checking && !deleting;
  const blockers = check?.blockers ?? [];
  const purgeable = check?.purgeable ?? [];

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <DeleteForeverIcon />
        <Typography variant="h6" component="span" fontWeight="bold">
          {t('current.deleteAccount.title')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {accountName || '-'}
            </Typography>
            {accountCode && (
              <Chip size="small" label={accountCode} sx={{ mt: 0.5 }} variant="outlined" />
            )}
          </Box>

          <Divider />

          {checking && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                {t('current.deleteAccount.checking')}
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!checking && check && !check.canDelete && (
            <>
              <Alert severity="error" icon={<BlockIcon />}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>
                  {t('current.deleteAccount.blockedTitle')}
                </AlertTitle>
                {t('current.deleteAccount.blockedDescription')}
              </Alert>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {t('current.deleteAccount.blockersHeading')}
                </Typography>
                <List dense disablePadding>
                  {blockers.map((relation) => (
                    <ListItem key={relation.code} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 26 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 10 }} color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          relation.code === 'balance' || relation.code === 'systemAccount'
                            ? relationLabel(relation)
                            : `${relationLabel(relation)}: ${relation.count}`
                        }
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Alert severity="info">{t('current.deleteAccount.blockedHint')}</Alert>
            </>
          )}

          {!checking && check?.canDelete && (
            <>
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>
                  {t('current.deleteAccount.irreversibleTitle')}
                </AlertTitle>
                {t('current.deleteAccount.irreversibleDescription')}
              </Alert>

              {purgeable.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <CleaningServicesIcon fontSize="small" color="warning" />
                    {t('current.deleteAccount.purgeHeading')}
                  </Typography>
                  <List dense disablePadding>
                    {purgeable.map((relation) => (
                      <ListItem key={relation.code} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 26 }}>
                          <FiberManualRecordIcon sx={{ fontSize: 10 }} color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${relationLabel(relation)}: ${relation.count}`}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Typography variant="body2">
                {t('current.deleteAccount.confirmQuestion', { name: accountName })}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting}>
          {check && !check.canDelete ? t('current.actions.close') : t('current.actions.cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={!canDelete}
          startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
        >
          {deleting ? t('current.deleteAccount.deleting') : t('current.deleteAccount.confirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
